import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import DataLookupField from '../../../components/DataLookupField';
import {
  Plus, Edit, Trash2, Save, Search, Download, X,
  FileText, Users, ChevronRight, ChevronDown, Bell,
  Lock, Eye, Pencil, Settings, Package, DollarSign, ExternalLink
} from 'lucide-react';

export function AddressTypeAhead({ value, country, onChange, onSelect }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const timerRef = React.useRef(null);

  const doSearch = (q) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!q || q.length < 3) { setSuggestions([]); return; }
    timerRef.current = setTimeout(async () => {
      try {
        const countryCode = (country || '').toLowerCase().includes('united states') ? 'us'
          : (country || '').toLowerCase().includes('united kingdom') ? 'gb' : '';
        const params = new URLSearchParams({ q, format: 'json', addressdetails: '1', limit: '6' });
        if (countryCode) params.append('countrycodes', countryCode);
        const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
          headers: { 'Accept-Language': 'en' }
        });
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data.map(d => ({
            display: d.display_name,
            address: [d.address?.road, d.address?.house_number].filter(Boolean).join(' ') || d.display_name.split(',')[0],
            city: d.address?.city || d.address?.town || d.address?.village || '',
            state: d.address?.state || '',
            zip: d.address?.postcode || '',
          })));
        }
      } catch { setSuggestions([]); }
    }, 350);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input value={value}
          onChange={e => { onChange(e.target.value); doSearch(e.target.value); setOpen(true); }}
          onFocus={() => { if (value && value.length >= 3) doSearch(value); setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 250)}
          className="text-sm pr-8" data-testid="env-company-address" placeholder="Start typing to search addresses..." />
        <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-[100] top-full left-0 right-0 mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button key={i} className="w-full text-left px-3 py-2 text-xs hover:bg-accent border-b last:border-b-0"
              onMouseDown={() => { onSelect(s); setOpen(false); }}>
              {s.display}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function AddressLookupInput({ value, country, onChange, onAddressFound }) {
  const [loading, setLoading] = useState(false);

  const lookupAddress = async (zip) => {
    onChange(zip);
    if (!zip || zip.length < 3) return;
    setLoading(true);
    try {
      const c = (country || '').toLowerCase();
      if (c.includes('united kingdom') || c.includes('uk')) {
        const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(zip)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.result) {
            onAddressFound({
              city: data.result.admin_district || data.result.parish || '',
              state: data.result.region || data.result.country || '',
            });
          }
        }
      } else if (c.includes('united states') || c.includes('us') || c.includes('usa')) {
        const res = await fetch(`https://api.zippopotam.us/us/${encodeURIComponent(zip)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.places && data.places[0]) {
            onAddressFound({
              city: data.places[0]['place name'] || '',
              state: data.places[0]['state'] || '',
            });
          }
        }
      }
    } catch { /* silently fail */ }
    setLoading(false);
  };

  return (
    <div className="relative">
      <Input value={value} onChange={e => lookupAddress(e.target.value)}
        className="text-sm pr-8" data-testid="env-company-zip" placeholder="Enter zip/postcode to auto-fill" />
      {loading && <div className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />}
      {!loading && <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />}
    </div>
  );
}

export function DefaultValueLookup({ field, value, onChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);

  const INCOTERMS = ['EXW','FCA','FAS','FOB','CFR','CIF','CPT','CIP','DAP','DPU','DDP'];
  const SHIPPING_METHODS = ['Air Freight','Sea Freight','Road Freight','Rail Freight','Express/Courier','Parcel Post','Hand Delivery','Collect','LTL','FTL','Dropship'];

  const doSearch = async (q) => {
    if (!field.api) { setSuggestions([]); return; }
    try {
      const res = await api.get(field.api, { params: { search: q || '', page_size: 10 } });
      const data = res.data.data || res.data || [];
      setSuggestions(Array.isArray(data) ? data.slice(0, 10) : []);
    } catch { setSuggestions([]); }
  };

  if (field.type === 'select' || field.type === 'budget') {
    const budgetOpts = ['In Budget', 'Out of Budget', 'Over Budget'];
    return (
      <div className="space-y-1">
        <Label className="text-xs">{field.label}</Label>
        <select value={value} onChange={e => onChange(e.target.value)}
          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid={`env-${field.key}`}>
          <option value="">-- Select --</option>
          {field.type === 'budget' ? budgetOpts.map(o => <option key={o} value={o}>{o}</option>)
            : null}
        </select>
      </div>
    );
  }

  if (field.type === 'incoterms') {
    return (
      <div className="space-y-1">
        <Label className="text-xs">{field.label}</Label>
        <select value={value} onChange={e => onChange(e.target.value)}
          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid={`env-${field.key}`}>
          <option value="">-- Select --</option>
          {INCOTERMS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
    );
  }

  if (field.type === 'shipping') {
    return (
      <div className="space-y-1">
        <Label className="text-xs">{field.label}</Label>
        <select value={value} onChange={e => onChange(e.target.value)}
          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid={`env-${field.key}`}>
          <option value="">-- Select --</option>
          {SHIPPING_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
    );
  }

  if (!field.api) {
    return (
      <div className="space-y-1">
        <Label className="text-xs">{field.label}</Label>
        <Input value={value} onChange={e => onChange(e.target.value)} className="text-sm" data-testid={`env-${field.key}`} />
      </div>
    );
  }

  return (
    <div className="space-y-1 relative">
      <Label className="text-xs">{field.label}</Label>
      <div className="relative">
        <Input value={value}
          onChange={e => { onChange(e.target.value); doSearch(e.target.value); setOpen(true); }}
          onFocus={() => { doSearch(value || ''); setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 250)}
          className="text-sm pr-8" data-testid={`env-${field.key}`} />
        <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-[200] top-full left-0 right-0 mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto"
          onMouseDown={e => e.preventDefault()}>
          {suggestions.map((s, i) => (
            <div key={i} className="w-full text-left px-3 py-2 text-sm hover:bg-accent border-b last:border-b-0 cursor-pointer"
              onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onChange(s.code || s.value || s); setOpen(false); }}>
              <span className="font-mono text-primary text-xs font-medium">{s.code || s.value || s}</span>
              {s.description && <span className="text-muted-foreground text-xs ml-2">— {s.description}</span>}
              {s.name && !s.description && <span className="text-muted-foreground text-xs ml-2">— {s.name}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function CompanyLogoUpload() {
  const [logoUrl, setLogoUrl] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    // Load current logo
    api.get('/api/system/company-logo').then(r => {
      if (r.data?.url) setLogoUrl(r.data.url);
    }).catch(() => {});
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error('File too large. Maximum 500 KB.'); return; }
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'].includes(file.type)) {
      toast.error('Unsupported format. Use JPG, PNG, GIF, WebP, or SVG.'); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/api/system/company-logo', fd);
      setLogoUrl(res.data.url + '?t=' + Date.now());
      toast.success('Logo uploaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const handleDelete = async () => {
    try {
      await api.delete('/api/system/company-logo');
      setLogoUrl(null);
      toast.success('Logo deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div>
      <h4 className="text-sm font-medium mb-3">Company Logo</h4>
      <div
        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
          dragging ? 'border-primary bg-primary/10' : 'border-muted-foreground/30 bg-muted/10'
        }`}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
      >
        {/* Preview */}
        <div className="w-full h-28 flex items-center justify-center mb-3 bg-muted/20 rounded overflow-hidden">
          {logoUrl ? (
            <img src={logoUrl} alt="Company logo" className="max-h-full max-w-full object-contain" />
          ) : (
            <span className="text-muted-foreground text-xs">{uploading ? 'Uploading...' : 'No logo set'}</span>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground mb-1">Max 500 KB · 315×120px recommended</p>
        <p className="text-[10px] text-muted-foreground mb-2">Drag & drop an image here, or:</p>
        <input type="file" ref={fileRef} accept="image/*" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
        <div className="flex gap-2 justify-center">
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? 'Uploading...' : 'Select File'}
          </Button>
          {logoUrl && (
            <Button variant="destructive" size="sm" onClick={handleDelete}>
              <Trash2 className="h-3 w-3 mr-1" /> Delete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}


export function EnvironmentSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('expenditure-code-names');

  useEffect(() => { fetchSettings(); }, []);

  const isDirty = useMemo(() => {
    if (!settings || !originalSettings) return false;
    try { return JSON.stringify(settings) !== JSON.stringify(originalSettings); }
    catch { return false; }
  }, [settings, originalSettings]);

  // Browser-level guard against losing unsaved changes
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/environment-settings');
      const s = res.data.settings || {};
      const defaults = getDefaults();
      // Ensure expenditure code soft labels always have values
      for (const k of ['exp_code_1','exp_code_2','exp_code_3','exp_code_4','exp_code_5','exp_code_6','cost_centre_name']) {
        if (!s[k]) s[k] = defaults[k];
      }
      const merged = { ...defaults, ...s };
      setSettings(merged);
      setOriginalSettings(merged);
    } catch (err) { setSettings(getDefaults()); setOriginalSettings(getDefaults()); }
    setLoading(false);
  };

  const getDefaults = () => {
    const year = new Date().getFullYear();
    return {
      asset_depreciation_method: 'straight_line',
      company_address: '', company_city: '', company_state: '', company_zip: '', company_country: '',
      currency_exchange_rate_days: 0,
      default_delivery_point: '', default_gl: '', default_invoice_point: '', default_property_code: '',
      default_document_type: '', default_nominal_code: '', default_cost_centre: '', default_category: '',
      default_account: '', default_budget: '', default_restaurant: '', default_incoterms: '',
      default_department_code: '', default_shipping_method: '',
      exp_code_1: 'Account', exp_code_2: 'Restaurant', exp_code_3: 'Department Code',
      cost_centre_name: 'Cost Centre',
      exp_code_4: 'GL', exp_code_5: 'Property Code', exp_code_6: 'Nominal Code',
      default_sales_tax: 0, default_tax_pick: 'delivery', price_type: 'net',
      financial_period_start: `${year}-01-01`, financial_period_end: `${year}-12-31`,
      financial_auto_increment: false,
      email_trailer_text: '',
      dept_email_purchasing: '', dept_email_purchasing_enabled: false,
      dept_email_accounts: '', dept_email_accounts_enabled: false,
      dept_email_receiving: ''
    };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Strip MongoDB internal fields before saving
      const { _id, id, type, ...payload } = settings;
      await api.put('/api/admin/environment-settings', payload);
      toast.success('Environment settings saved');
      setOriginalSettings(settings);
      refreshLabels();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const handleDiscard = () => {
    if (originalSettings) {
      setSettings(originalSettings);
      toast.info('Changes discarded');
    }
  };

  const update = (field, value) => setSettings(p => ({ ...p, [field]: value }));
  const { labels: expLabels, refresh: refreshLabels } = useExpenditureLabels();

  if (loading) return <LoadingSpinner />;

  const defaultFields = [
    { label: 'Delivery Point', key: 'default_delivery_point', api: '/api/data/delivery-points' },
    { label: expLabels.exp_code_4, key: 'default_gl', api: '/api/account-codes/4' },
    { label: 'Invoice Point', key: 'default_invoice_point', api: '/api/data/invoice-points' },
    { label: expLabels.exp_code_5, key: 'default_property_code', api: '/api/account-codes/5' },
    { label: 'Document Type', key: 'default_document_type', api: '/api/data/document-types' },
    { label: expLabels.exp_code_6, key: 'default_nominal_code', api: '/api/account-codes/6' },
    { label: expLabels.cost_centre, key: 'default_cost_centre', api: '/api/account-codes/7' },
    { label: 'Category', key: 'default_category', api: '/api/data/categories' },
    { label: expLabels.exp_code_1, key: 'default_account', api: '/api/account-codes/1' },
    { label: 'Budget', key: 'default_budget', type: 'budget' },
    { label: expLabels.exp_code_2, key: 'default_restaurant', api: '/api/account-codes/2' },
    { label: 'Incoterms', key: 'default_incoterms', type: 'incoterms' },
    { label: expLabels.exp_code_3, key: 'default_department_code', api: '/api/account-codes/3' },
    { label: 'Shipping Method', key: 'default_shipping_method', type: 'shipping' },
  ];

  // Tab definitions — clicking a tab replaces the visible section
  const tabs = [
    { id: 'asset-register', label: 'Asset Register' },
    { id: 'company-logo', label: 'Company Info' },
    { id: 'expenditure-code-names', label: 'Expenditure Codes' },
    { id: 'default-values', label: 'Defaults' },
    { id: 'currency-exchange', label: 'Currency' },
    { id: 'decimal-places', label: 'Decimal Places' },
    { id: 'email-options', label: 'Email' },
    { id: 'smtp-settings', label: 'SMTP' },
    { id: 'finance-settings', label: 'Finance' },
    { id: 'financial-period', label: 'Financial Period' },
    { id: 're-sequence-numbers', label: 'Re-Sequence' },
    { id: 'select-time-zone', label: 'Time Zone' },
    { id: 'system-security', label: 'Security' },
    { id: 'system-switches', label: 'Switches' },
    { id: 'savings-config', label: 'Savings' },
    { id: 'external-portal-urls', label: 'Portals' },
  ];
  const isActive = (id) => activeTab === id;

  return (
    <div className="max-w-5xl pb-4" data-testid="environment-settings-panel">
      <h1 className="text-2xl font-bold mb-1">Environment Settings</h1>
      <p className="text-lg font-medium text-muted-foreground mb-4">System Configuration</p>

      {/* Sticky tab navigation — click a tab to show only that section */}
      <div className="sticky top-0 z-20 -mx-6 px-6 py-2 bg-white/95 backdrop-blur-sm border-b border-zinc-200 mb-6">
        <div className="flex flex-wrap gap-1.5">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              data-testid={`env-tab-${t.id}`}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap ${
                activeTab === t.id
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Section 1: Asset Register */}
      <div className={`border rounded-lg p-6 mb-6 ${isActive('asset-register') ? '' : 'hidden'}`} data-testid="env-asset-register-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">1</span>
          Asset Register
        </h3>
        <div className="flex items-start gap-8 mb-4">
          <Label className="w-48 text-sm pt-1">Asset Depreciation Method</Label>
          <p className="text-sm text-muted-foreground flex-1">Asset is depreciated using the below 2 methods:</p>
        </div>
        <div className="space-y-4 ml-52">
          <div className="border rounded-lg p-4">
            <h4 className="text-sm font-semibold mb-2">Straight Line</h4>
            <p className="text-xs text-muted-foreground">
              The simplest and most commonly used, straight line depreciation is calculated by taking the purchase price of an
              asset subtracted by the (optional) salvage value divided by the total productive years the asset can be reasonably
              expected to benefit the company.
            </p>
            <p className="text-xs text-muted-foreground mt-2 font-mono">
              purchase price – approximate salvage value (Can be zero)<br />
              ————————— (divided by) —————————<br />
              estimated useful life of asset
            </p>
          </div>
          <div className="border rounded-lg p-4">
            <h4 className="text-sm font-semibold mb-2">Reducing Balance</h4>
            <p className="text-xs text-muted-foreground">
              Rather than charging a fixed amount every year, a (fixed) percentage of the remaining value of the asset is charged
              every year best illustrated by an example. An asset initially worth 10,000 USD is depreciated at 25% p.a. so will be
              depreciated by 2,500 USD in the first year, but by 25% x 7,500 USD (or 10,000 - 2,500) in the second year, etc...
            </p>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button variant="outline" size="sm" data-testid="depreciate-assets-btn">
            <DollarSign className="h-4 w-4 mr-1" /> Depreciate Assets
          </Button>
        </div>
      </div>

      {/* Section 2: Company Logo / Address */}
      <div className={`border rounded-lg p-6 mb-6 ${isActive('company-logo') ? '' : 'hidden'}`} data-testid="env-company-logo-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">2</span>
          Company Logo / Address
        </h3>
        <div className="grid grid-cols-2 gap-6">
          {/* Left: Company Logo */}
          <CompanyLogoUpload />
          {/* Right: Company Address */}
          <div>
            <h4 className="text-sm font-medium mb-3">Company Address</h4>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Country/Region</Label>
                  <select value={settings.company_country || ''} onChange={(e) => update('company_country', e.target.value)}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="env-company-country">
                    <option value="">-- Select --</option>
                    <option value="United States">United States</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Canada">Canada</option>
                    <option value="Australia">Australia</option>
                    <option value="Germany">Germany</option>
                    <option value="France">France</option>
                    <option value="India">India</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Zip/Postal Code</Label>
                  <AddressLookupInput
                    value={settings.company_zip || ''}
                    country={settings.company_country || ''}
                    onChange={(zip) => update('company_zip', zip)}
                    onAddressFound={(addr) => {
                      if (addr.city) update('company_city', addr.city);
                      if (addr.state) update('company_state', addr.state);
                    }}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Address</Label>
                <AddressTypeAhead
                  value={settings.company_address || ''}
                  country={settings.company_country || ''}
                  onChange={(v) => update('company_address', v)}
                  onSelect={(addr) => {
                    update('company_address', addr.address);
                    if (addr.city) update('company_city', addr.city);
                    if (addr.state) update('company_state', addr.state);
                    if (addr.zip) update('company_zip', addr.zip);
                  }}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">City</Label>
                  <Input value={settings.company_city || ''} onChange={(e) => update('company_city', e.target.value)}
                    className="text-sm" data-testid="env-company-city" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">State/Province</Label>
                  <Input value={settings.company_state || ''} onChange={(e) => update('company_state', e.target.value)}
                    className="text-sm" data-testid="env-company-state" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: Currency Exchange Rate */}
      <div className={`border rounded-lg p-6 mb-6 ${isActive('currency-exchange') ? '' : 'hidden'}`} data-testid="env-currency-exchange-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">3</span>
          Currency Exchange Rate
        </h3>
        <div className="flex items-center gap-4 mb-4">
          <Label className="w-56 text-sm">Set Auto Currency Exchange Rate</Label>
          <select
            value={settings.currency_exchange_rate_days || 0}
            onChange={(e) => update('currency_exchange_rate_days', parseInt(e.target.value))}
            className="w-20 h-9 rounded-md border border-input bg-background px-2 text-sm"
            data-testid="env-exchange-rate-days"
          >
            {[0,1,2,3,5,7,14,30].map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <span className="text-sm text-muted-foreground">day(s)</span>
        </div>
        <div className="ml-60 space-y-1 text-xs text-muted-foreground">
          <p>You can get live currency exchange rates from Google's web Currency API.</p>
          <p>Select how often to update the rates as the number of days from the drop down list. If the value is 0, the currency will not be updated at all.</p>
          <p>Dropdown value = 0 (auto update currency is set OFF)</p>
          <p>Dropdown value {'>'} 0 (auto update currency is set to the dropdown value in days)</p>
        </div>
      </div>

      {/* Decimal Places Setting */}
      <div className={`border rounded-lg p-6 mb-6 ${isActive('decimal-places') ? '' : 'hidden'}`} data-testid="env-decimal-places-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">4</span>
          Decimal Places
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Define the number of decimal places used for displaying values across the system (prices, quantities, tax rates, discounts, exchange rates). 
          Totals (requisition totals, order totals, invoice totals, report totals) will always display to 2 decimal places.
        </p>
        <div className="flex items-center gap-4">
          <Label className="w-56 text-sm">System Decimal Places</Label>
          <select value={settings.decimal_places || 2} onChange={e => update('decimal_places', parseInt(e.target.value))}
            className="w-20 h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="env-decimal-places">
            {[2, 3, 4, 5, 6].map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <span className="text-sm text-muted-foreground">Example: {Number(123.456789).toFixed(settings.decimal_places || 2)}</span>
        </div>
      </div>

      {/* Section 5: Expenditure Code Names (define labels before using them) */}
      <div className={`border rounded-lg p-6 mb-6 ${isActive('expenditure-code-names') ? '' : 'hidden'}`} data-testid="env-expenditure-code-names-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">5</span>
          Expenditure Code Names
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          There are six distinct Expenditure Code Files and a Cost Code File which you may optionally use for any business division or area of activity for which it is desirable to allocate costs.
        </p>
        <p className="text-sm text-muted-foreground mb-4">
          Classifying your expenditure/cost into meaningful group(s) provides a method of identifying various types of costs for reporting and analysis purposes. You can rename each file so that all screens and reports display your terminology i.e. Budget Code, Job Number, Project Code, etc.
        </p>
        <div className="grid grid-cols-3 gap-6">
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Expenditure Code for Header</h4>
            <div className="space-y-3">
              {[{k:'exp_code_1',l:'Expenditure Code 1',d:'Account'},{k:'exp_code_2',l:'Expenditure Code 2',d:'Restaurant'},{k:'exp_code_3',l:'Expenditure Code 3',d:'Department Code'}].map(f=>(
                <div key={f.k} className="space-y-1">
                  <Label className="text-xs">{f.l}</Label>
                  <Input value={settings[f.k]||f.d} onChange={e=>update(f.k,e.target.value)} className="text-sm" data-testid={`env-${f.k}`} />
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Cost Code</h4>
            <div className="space-y-1">
              <Label className="text-xs">Cost Centre</Label>
              <Input value={settings.cost_centre_name||'Cost Centre'} onChange={e=>update('cost_centre_name',e.target.value)} className="text-sm" data-testid="env-cost_centre_name" />
            </div>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Expenditure Code for Line</h4>
            <div className="space-y-3">
              {[{k:'exp_code_4',l:'Expenditure Code 4',d:'GL'},{k:'exp_code_5',l:'Expenditure Code 5',d:'Property Code'},{k:'exp_code_6',l:'Expenditure Code 6',d:'Nominal Code'}].map(f=>(
                <div key={f.k} className="space-y-1">
                  <Label className="text-xs">{f.l}</Label>
                  <Input value={settings[f.k]||f.d} onChange={e=>update(f.k,e.target.value)} className="text-sm" data-testid={`env-${f.k}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Section 5: Default Values */}
      <div className={`border rounded-lg p-6 mb-6 ${isActive('default-values') ? '' : 'hidden'}`} data-testid="env-default-values-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">5</span>
          Default Values
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          A default is a value or setting that a system automatically inputs (usually on the creation of a new record), which you can either accept or override.
        </p>
        <div className="grid grid-cols-2 gap-4">
          {defaultFields.map(f => (
            <DefaultValueLookup key={f.key} field={f} value={settings[f.key] || ''} onChange={v => update(f.key, v)} />
          ))}
        </div>
      </div>

      {/* Section 6: Email Options */}
      <div className={`border rounded-lg p-6 mb-6 ${isActive('email-options') ? '' : 'hidden'}`} data-testid="env-email-options-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">6</span>
          Email Options
        </h3>

        {/* Email Trailer Text */}
        <div className="mb-6">
          <div className="flex items-start gap-4 mb-2">
            <Label className="w-32 text-sm pt-2">Email Trailer Text</Label>
            <div className="flex-1">
              <Textarea
                value={settings.email_trailer_text || ''}
                onChange={(e) => {
                  if (e.target.value.length <= 500) update('email_trailer_text', e.target.value);
                }}
                rows={4} className="text-sm"
                placeholder="Appended to the body of an email for use as notes or a disclaimer"
                data-testid="env-email-trailer-text"
              />
              <p className="text-xs text-muted-foreground text-right mt-1">
                characters remaining {500 - (settings.email_trailer_text?.length || 0)}
              </p>
            </div>
            <span className="text-xs text-muted-foreground w-48 pt-2">
              (Appended to the body of an email for use as notes or a disclaimer - maximum 500 characters)
            </span>
          </div>
        </div>

        {/* Department Email Addresses */}
        <div className="border-t pt-4">
          <h4 className="text-sm font-semibold mb-4">Department Email Addresses</h4>
          <div className="space-y-4">
            {/* Purchasing */}
            <div className="flex items-start gap-4">
              <Label className="w-24 text-sm pt-2">Purchasing</Label>
              <Input
                value={settings.dept_email_purchasing || ''}
                onChange={(e) => update('dept_email_purchasing', e.target.value)}
                className="w-56 text-sm" placeholder="email@company.com"
                data-testid="env-email-purchasing"
              />
              <div className="flex items-center gap-2">
                <Switch
                  checked={settings.dept_email_purchasing_enabled}
                  onCheckedChange={(v) => update('dept_email_purchasing_enabled', v)}
                  data-testid="env-email-purchasing-toggle"
                />
                <span className={`text-xs font-medium ${settings.dept_email_purchasing_enabled ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {settings.dept_email_purchasing_enabled ? 'Yes' : 'No'}
                </span>
              </div>
              <p className="flex-1 text-[11px] text-muted-foreground">
                If switched to 'Yes', when a new request is created and approved, an email will be fired to the purchasing department.
              </p>
            </div>
            {/* Accounts */}
            <div className="flex items-start gap-4">
              <Label className="w-24 text-sm pt-2">Accounts</Label>
              <Input
                value={settings.dept_email_accounts || ''}
                onChange={(e) => update('dept_email_accounts', e.target.value)}
                className="w-56 text-sm" placeholder="email@company.com"
                data-testid="env-email-accounts"
              />
              <div className="flex items-center gap-2">
                <Switch
                  checked={settings.dept_email_accounts_enabled}
                  onCheckedChange={(v) => update('dept_email_accounts_enabled', v)}
                  data-testid="env-email-accounts-toggle"
                />
                <span className={`text-xs font-medium ${settings.dept_email_accounts_enabled ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {settings.dept_email_accounts_enabled ? 'Yes' : 'No'}
                </span>
              </div>
              <p className="flex-1 text-[11px] text-muted-foreground">
                Select if you want to send a copy of purchase order to accounts email address.
              </p>
            </div>
            {/* Receiving */}
            <div className="flex items-start gap-4">
              <Label className="w-24 text-sm pt-2">Receiving</Label>
              <Input
                value={settings.dept_email_receiving || ''}
                onChange={(e) => update('dept_email_receiving', e.target.value)}
                className="w-56 text-sm" placeholder="email@company.com"
                data-testid="env-email-receiving"
              />
              <p className="flex-1 text-[11px] text-muted-foreground ml-16">
                Add a Receiving Department email, if you want to send an email notification whenever an item is booked-in.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 7: Finance Settings */}
      <div className={`border rounded-lg p-6 mb-6 ${isActive('finance-settings') ? '' : 'hidden'}`} data-testid="env-finance-settings-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">7</span>
          Finance Settings
        </h3>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Label className="w-52 text-sm">System Base Currency Code</Label>
            <span className="text-sm font-medium">{settings.system_currency_code || 'USD'}</span>
            <span className="text-xs text-muted-foreground">(Corresponds to your local monetary system with a fixed exchange rate of 1.0)</span>
          </div>
          <div className="flex items-center gap-4">
            <Label className="w-52 text-sm">Currency Name</Label>
            <span className="text-sm font-medium">{settings.system_currency_name || 'US Dollars'}</span>
            <span className="text-xs text-muted-foreground">(Descriptive Name)</span>
          </div>
          <p className="text-xs text-red-500">System Base Currency Code and Currency Name cannot be modified, since there are live orders on this system.</p>
          <div className="border-t pt-4 flex items-center gap-4">
            <Label className="w-52 text-sm">Default Sales Tax%</Label>
            <Input type="number" step="0.01" min="0" max="99" value={settings.default_sales_tax||0} onChange={e=>update('default_sales_tax',parseFloat(e.target.value)||0)}
              className="w-24 text-sm" data-testid="env-default-sales-tax" />
            <span className="text-xs text-muted-foreground">(You can enter between 0% and 99% for use as the default tax rate)</span>
          </div>
          <div className="flex items-start gap-4">
            <Label className="w-52 text-sm pt-2">Default Tax Pick</Label>
            <div className="space-y-2">
              {['delivery','supplier'].map(v=>(
                <label key={v} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="default_tax_pick" value={v} checked={settings.default_tax_pick===v} onChange={e=>update('default_tax_pick',e.target.value)} className="h-4 w-4" data-testid={`env-tax-pick-${v}`} />
                  <span className="text-sm capitalize">{v}</span>
                </label>
              ))}
            </div>
            <span className="text-xs text-muted-foreground pt-2">(Pick default tax rate from Delivery File or Supplier File)</span>
          </div>
          <div className="flex items-start gap-4">
            <Label className="w-52 text-sm pt-2">Select the Price Type</Label>
            <div className="space-y-2">
              {[{v:'net',l:'Net Price'},{v:'gross',l:'Gross Price'}].map(o=>(
                <label key={o.v} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="price_type" value={o.v} checked={settings.price_type===o.v} onChange={e=>update('price_type',e.target.value)} className="h-4 w-4" data-testid={`env-price-type-${o.v}`} />
                  <span className="text-sm">{o.l}</span>
                </label>
              ))}
            </div>
            <span className="text-xs text-muted-foreground pt-2">(Select the price type you want the system to display)</span>
          </div>
        </div>
      </div>

      {/* Section 8: Financial Period */}
      <div className={`border rounded-lg p-6 mb-6 ${isActive('financial-period') ? '' : 'hidden'}`} data-testid="env-financial-period-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">8</span>
          Financial Period
        </h3>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Label className="w-40 text-sm">Financial Period</Label>
            <Input type="date" value={settings.financial_period_start||''} onChange={e=>update('financial_period_start',e.target.value)} className="w-44 text-sm" data-testid="env-fin-period-start" />
            <span className="text-sm text-muted-foreground">to</span>
            <Input type="date" value={settings.financial_period_end||''} onChange={e=>update('financial_period_end',e.target.value)} className="w-44 text-sm" data-testid="env-fin-period-end" />
          </div>
          <div className="flex items-center gap-4">
            <Label className="w-40 text-sm">Auto Increment</Label>
            <Switch checked={settings.financial_auto_increment||false} onCheckedChange={v=>update('financial_auto_increment',v)} data-testid="env-fin-auto-increment" />
            <span className={`text-sm font-medium ${settings.financial_auto_increment ? 'text-green-600' : 'text-muted-foreground'}`}>
              {settings.financial_auto_increment ? 'Yes' : 'No'}
            </span>
          </div>
          <p className="text-xs text-muted-foreground ml-44">
            Finance Period - Auto increment will reset the new fiscal year as soon as it hits the first day of the new fiscal year.
          </p>
        </div>
      </div>

      {/* Section 9: Re-Sequence Numbers */}
      <div className={`border rounded-lg p-6 mb-6 ${isActive('re-sequence-numbers') ? '' : 'hidden'}`} data-testid="env-re-sequence-numbers-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">9</span>
          Re-Sequence Numbers
        </h3>
        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <Label className="w-40 text-sm">Orders On File</Label>
            <span className="text-sm font-medium" data-testid="env-orders-on-file">—</span>
          </div>
          <div className="flex items-center gap-4">
            <Label className="w-40 text-sm">Last Order Number</Label>
            <span className="text-sm font-medium" data-testid="env-last-order-number">—</span>
          </div>
          <div className="border-t pt-3 flex items-center gap-4">
            <Label className="w-40 text-sm">New Order Number</Label>
            <Input type="number" value={settings.new_order_number||''} onChange={e=>update('new_order_number',parseInt(e.target.value)||0)} className="w-40 text-sm" data-testid="env-new-order-number" />
          </div>
        </div>
      </div>

      {/* Section 10: SMTP Settings */}
      <div className={`border rounded-lg p-6 mb-6 ${isActive('smtp-settings') ? '' : 'hidden'}`} data-testid="env-smtp-settings-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">10</span>
          SMTP Settings
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          You have 2 options to send emails. If you want to use your own email server, fill in the SMTP details below. If users want outbound emails sent from their own email accounts, enable Individual User Email Accounts.
        </p>
        <div className="flex items-start gap-4 mb-4">
          <Label className="w-52 text-sm pt-2">Select Outbound Email Option</Label>
          <div className="space-y-2">
            {[{v:'smtp',l:'Use SMTP Server'},{v:'individual',l:'Enable Individual User Email Accounts'}].map(o=>(
              <label key={o.v} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="smtp_option" value={o.v} checked={(settings.smtp_option||'smtp')===o.v} onChange={e=>update('smtp_option',e.target.value)} className="h-4 w-4" data-testid={`env-smtp-option-${o.v}`} />
                <span className="text-sm">{o.l}</span>
              </label>
            ))}
          </div>
        </div>
        {(settings.smtp_option||'smtp') === 'smtp' && (
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-start gap-4">
              <Label className="w-52 text-sm pt-2">SMTP Server Name (HOST)</Label>
              <div className="flex-1">
                <Input value={settings.smtp_host||''} onChange={e=>update('smtp_host',e.target.value)} className="text-sm" placeholder="smtp.gmail.com" data-testid="env-smtp-host" />
                <p className="text-xs text-muted-foreground mt-1">The host name for your SMTP server</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Label className="w-52 text-sm pt-2">Port</Label>
              <div className="flex-1">
                <Input type="number" value={settings.smtp_port||587} onChange={e=>update('smtp_port',parseInt(e.target.value)||587)} className="w-24 text-sm" data-testid="env-smtp-port" />
                <p className="text-xs text-muted-foreground mt-1">The port number used by the SMTP server (e.g. 587, 465, 25)</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Label className="w-52 text-sm pt-2">Email Account - User ID</Label>
              <div className="flex-1">
                <Input value={settings.smtp_email||''} onChange={e=>update('smtp_email',e.target.value)} className="text-sm" placeholder="donotreply@eyvo.com" data-testid="env-smtp-email" />
                <p className="text-xs text-muted-foreground mt-1">Unique username or email to log in to the SMTP server</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Label className="w-52 text-sm pt-2">Network Credentials - Password</Label>
              <div className="flex-1">
                <Input type="password" value={settings.smtp_password||''} onChange={e=>update('smtp_password',e.target.value)} className="text-sm" data-testid="env-smtp-password" />
                <p className="text-xs text-muted-foreground mt-1">Unique password to log in to the SMTP server</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Label className="w-52 text-sm pt-2">SSL</Label>
              <div className="space-y-2">
                {[{v:'true',l:'True'},{v:'false',l:'False'}].map(o=>(
                  <label key={o.v} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="smtp_ssl" value={o.v} checked={(settings.smtp_ssl||'true')===o.v} onChange={e=>update('smtp_ssl',e.target.value)} className="h-4 w-4" data-testid={`env-smtp-ssl-${o.v}`} />
                    <span className="text-sm">{o.l}</span>
                  </label>
                ))}
              </div>
              <span className="text-xs text-muted-foreground pt-2">(Select true if SSL certificate is installed on the server)</span>
            </div>
          </div>
        )}
      </div>

      {/* Section 11: Select Time Zone */}
      <div className={`border rounded-lg p-6 mb-6 ${isActive('select-time-zone') ? '' : 'hidden'}`} data-testid="env-select-time-zone-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">11</span>
          Select Time Zone
        </h3>
        <div className="flex items-center gap-4">
          <Label className="w-40 text-sm">Select your Time Zone</Label>
          <select value={settings.timezone||'UTC-08:00'} onChange={e=>update('timezone',e.target.value)}
            className="w-96 h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="env-timezone">
            {[
              '(UTC-12:00) International Date Line West','(UTC-11:00) Samoa','(UTC-10:00) Hawaii',
              '(UTC-09:00) Alaska','(UTC-08:00) Pacific Time (US & Canada)','(UTC-07:00) Mountain Time (US & Canada)',
              '(UTC-06:00) Central Time (US & Canada)','(UTC-05:00) Eastern Time (US & Canada)',
              '(UTC-04:00) Atlantic Time (Canada)','(UTC-03:00) Buenos Aires','(UTC-02:00) Mid-Atlantic',
              '(UTC-01:00) Azores','(UTC+00:00) London, Dublin, Lisbon','(UTC+01:00) Berlin, Paris, Rome',
              '(UTC+02:00) Cairo, Helsinki','(UTC+03:00) Moscow, Kuwait','(UTC+04:00) Abu Dhabi, Muscat',
              '(UTC+05:00) Islamabad, Karachi','(UTC+05:30) Mumbai, Chennai, Kolkata',
              '(UTC+06:00) Dhaka, Almaty','(UTC+07:00) Bangkok, Hanoi','(UTC+08:00) Beijing, Singapore',
              '(UTC+09:00) Tokyo, Seoul','(UTC+09:30) Adelaide, Darwin','(UTC+10:00) Sydney, Melbourne',
              '(UTC+11:00) Solomon Islands','(UTC+12:00) Auckland, Fiji'
            ].map(tz=><option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>
      </div>

      {/* Section 12: System Security */}
      <div className={`border rounded-lg p-6 mb-6 ${isActive('system-security') ? '' : 'hidden'}`} data-testid="env-system-security-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">12</span>
          System Security
        </h3>
        <div className="space-y-4">
          {[
            {k:'secure_login',l:'Secure Login',d:'Select this option if you want to activate CAPTCHA on login to protect your system against spambots.'},
            {k:'external_approval_login',l:'External Approval Login',d:'Select this option if you want approvers to login to the system and approve instead of opening the approval page directly from email.'},
            {k:'secure_password',l:'Secure Password',d:"Enabling this allows users to update their passwords. When switched to 'Yes', the 'Select Password Duration' option will be seen, allowing the System Administrator to set the number of months after which users must update their password."},
            {k:'login_with_otp',l:'Login with One Time Password (OTP)',d:'Select this option if you want users to login with One Time Password (OTP).'},
            {k:'ip_restriction',l:'IP Restriction',d:'Select the checkbox for the country(s) you want to grant application access. If you do not wish to grant access to the whole country but just an IP or multiple IPs, please type in the IPs separated with a comma in the IP Exception field.'},
          ].map(item=>(
            <div key={item.k} className="flex items-start gap-4 py-2 border-b last:border-b-0">
              <Label className="w-56 text-sm pt-1 flex-shrink-0">{item.l}</Label>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Switch checked={settings[item.k]||false} onCheckedChange={v=>update(item.k,v)} data-testid={`env-${item.k}`} />
                <span className={`text-xs font-medium w-6 ${settings[item.k] ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {settings[item.k] ? 'Yes' : 'No'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground flex-1">{item.d}</p>
            </div>
          ))}
          {/* Password Duration - shown when Secure Password is enabled */}
          {settings.secure_password && (
            <div className="flex items-start gap-4 py-2 border-b bg-muted/10 px-2 rounded">
              <Label className="w-56 text-sm pt-1 flex-shrink-0">Select Password Duration</Label>
              <select value={settings.password_duration_months || 3} onChange={e => update('password_duration_months', parseInt(e.target.value))}
                className="w-24 h-8 rounded-md border border-input bg-background px-2 text-sm" data-testid="env-password-duration">
                {[1, 2, 3, 6, 12].map(m => <option key={m} value={m}>{m} month{m > 1 ? 's' : ''}</option>)}
              </select>
              <p className="text-xs text-muted-foreground flex-1">Users will be forced to change their password after this many months. Passwords must contain at least 8 characters, including uppercase, lowercase, a number, and a special character.</p>
            </div>
          )}
          {/* Session Timeout */}
          <div className="flex items-start gap-4 py-2 border-t">
            <Label className="w-56 text-sm pt-1 flex-shrink-0">Session Idle Timeout</Label>
            <div className="flex items-center gap-3">
              <select value={settings.session_timeout_seconds || 3600} onChange={e => update('session_timeout_seconds', parseInt(e.target.value))}
                className="h-8 rounded border px-2 text-sm w-40" data-testid="env-session-timeout">
                <option value={300}>5 minutes</option>
                <option value={600}>10 minutes</option>
                <option value={900}>15 minutes</option>
                <option value={1800}>30 minutes</option>
                <option value={3600}>1 hour</option>
                <option value={7200}>2 hours</option>
                <option value={14400}>4 hours</option>
                <option value={28800}>8 hours</option>
              </select>
              <p className="text-xs text-muted-foreground flex-1">After this period of inactivity, the user will be automatically logged out and their session released. This also controls when a concurrent login block expires.</p>
            </div>
          </div>
          {/* Single Sign On */}
          <div className="flex items-start gap-4 py-2 border-t">
            <Label className="w-56 text-sm pt-1 flex-shrink-0">Single Sign On</Label>
            <div className="flex items-center gap-4 flex-wrap">
              {['None','Google','Azure AD SAML','Azure AD','OKTA SAML'].map(o=>(
                <label key={o} className="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" name="single_sign_on" value={o} checked={(settings.single_sign_on||'None')===o}
                    onChange={e=>update('single_sign_on',e.target.value)} className="h-4 w-4" data-testid={`env-sso-${o.toLowerCase().replace(/\s+/g,'-')}`} />
                  <span className="text-xs">{o}</span>
                </label>
              ))}
            </div>
          </div>
          {/* One-Click Approval */}
          <div className="flex items-start gap-4 py-2 border-t">
            <Label className="w-56 text-sm pt-1 flex-shrink-0">One-Click Approval</Label>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <Switch checked={!!settings.allow_oneclick_approval} onCheckedChange={v => update('allow_oneclick_approval', v)} data-testid="env-oneclick-approval" />
                <span className="text-xs">{settings.allow_oneclick_approval ? 'Enabled' : 'Disabled'}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">When enabled, approval emails include an encrypted link that allows approvers to approve or reject directly without logging in. Links are single-use, encrypted, and expire after 7 days. When disabled, approvers must log in to action approvals.</p>
            </div>
          </div>
          {/* Two-Factor Authentication */}
          <div className="flex items-start gap-4 py-2 border-t">
            <Label className="w-56 text-sm pt-1 flex-shrink-0">Two-Factor Authentication (2FA)</Label>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <Switch checked={!!settings.require_2fa} onCheckedChange={v => update('require_2fa', v)} data-testid="env-require-2fa" />
                <span className="text-xs">{settings.require_2fa ? 'Required' : 'Optional'}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">When set to Required, all users logging in with email and password must verify their identity with a one-time code from an authenticator app (Google Authenticator, Microsoft Authenticator, Authy, etc.). Users will be prompted to set up 2FA on their next login. This does not affect Google SSO logins.</p>
            </div>
          </div>
        </div>
      </div>
      <div className={`border rounded-lg p-6 mb-6 ${isActive('system-switches') ? '' : 'hidden'}`} data-testid="env-system-switches-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">13</span>
          System Switches
        </h3>
        <div className="space-y-3">
          {/* Toggle rows */}
          {[
            {k:'allow_self_registration',l:'Allow Self Registration',d:"Users can register themselves by clicking on the Self-Registration hyperlink (Add New User) provided on the login page. They will not be able to log in until a System Administrator assigns an available license to their account."},
            {k:'allow_self_approval',l:'Allow Self-Approval',d:"When enabled, users can approve their own requisitions and purchase orders. When disabled, users cannot approve documents they created — another approver must action them."},
          ].map(item=>(
            <div key={item.k} className="flex items-start gap-4 py-2 border-b">
              <Label className="w-56 text-sm pt-1 flex-shrink-0">{item.l}</Label>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Switch checked={settings[item.k]||false} onCheckedChange={v=>update(item.k,v)} data-testid={`env-${item.k}`} />
                <span className={`text-xs font-medium w-6 ${settings[item.k] ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {settings[item.k] ? 'Yes' : 'No'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground flex-1">{item.d}</p>
            </div>
          ))}
          {/* Auto Email Schedule (Days) - dropdown */}
          <div className="flex items-start gap-4 py-2 border-b">
            <Label className="w-56 text-sm pt-1 flex-shrink-0">Auto Email Schedule (Days)</Label>
            <select value={settings.auto_email_schedule_days||0} onChange={e=>update('auto_email_schedule_days',parseInt(e.target.value))}
              className="w-16 h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="env-auto_email_schedule_days">
              {[0,1,2,3,5,7,14,30].map(d=><option key={d} value={d}>{d}</option>)}
            </select>
            <p className="text-xs text-muted-foreground flex-1">Select the number of days that should pass before an approver receives an automatic email alert, notifying them that a request, purchase order, or invoice is still pending their approval.</p>
          </div>
          {/* Recurring Auto Email + more toggles */}
          {[
            {k:'recurring_auto_email',l:'Recurring Auto Email Schedule',d:'By activating this switch, the system will automatically send email reminders. The frequency will be based on the number of days specified in the Auto Email Schedule field.'},
          ].map(item=>(
            <div key={item.k} className="flex items-start gap-4 py-2 border-b">
              <Label className="w-56 text-sm pt-1 flex-shrink-0">{item.l}</Label>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Switch checked={settings[item.k]||false} onCheckedChange={v=>update(item.k,v)} data-testid={`env-${item.k}`} />
                <span className={`text-xs font-medium w-6 ${settings[item.k] ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {settings[item.k] ? 'Yes' : 'No'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground flex-1">{item.d}</p>
            </div>
          ))}
          {/* Data Listing Option - dropdown */}
          <div className="flex items-start gap-4 py-2 border-b">
            <Label className="w-56 text-sm pt-1 flex-shrink-0">Data Listing Option</Label>
            <select value={settings.data_listing_option||100} onChange={e=>update('data_listing_option',parseInt(e.target.value))}
              className="w-20 h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="env-data_listing_option">
              {[25,50,100,200,500].map(d=><option key={d} value={d}>{d}</option>)}
            </select>
            <p className="text-xs text-muted-foreground flex-1">Specify the desired number of records to be displayed on the main application's listing screen when presenting data.</p>
          </div>
          {/* Remaining toggles */}
          {[
            {k:'request_supplier_confirmation',l:'Request for Supplier Confirmation',d:'By selecting this switch, suppliers will be granted the ability to Accept or Reject Purchase Orders that are issued to them via email.'},
            {k:'supplier_delivery_tracking',l:'Supplier Delivery Tracking / Notes',d:'Enabling this switch allows the supplier to add delivery tracking information/notes after issuing the order.'},
          ].map(item=>(
            <div key={item.k} className="flex items-start gap-4 py-2 border-b">
              <Label className="w-56 text-sm pt-1 flex-shrink-0">{item.l}</Label>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Switch checked={settings[item.k]||false} onCheckedChange={v=>update(item.k,v)} data-testid={`env-${item.k}`} />
                <span className={`text-xs font-medium w-6 ${settings[item.k] ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {settings[item.k] ? 'Yes' : 'No'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground flex-1">{item.d}</p>
            </div>
          ))}
          {/* Text inputs: Tax Label, Reference, Job Title Label */}
          {[
            {k:'tax_label',l:'Tax Label',d:"Enter the desired tax label based on your specific requirements. e.g., 'Sales Tax', 'VAT', 'GST', etc.",df:'Sales Tax'},
            {k:'reference_label',l:'Reference',d:"Enter a reference number label. e.g., 'Ref Number', 'Tracking Number', 'Document Number', etc.",df:'Ref Num'},
            {k:'job_title_label',l:'Job Title Label',d:"Enter the desired Job Title label. e.g., 'Designation', 'Post', 'Position', etc.",df:'Job Title'},
          ].map(item=>(
            <div key={item.k} className="flex items-start gap-4 py-2 border-b">
              <Label className="w-56 text-sm pt-1 flex-shrink-0">{item.l}</Label>
              <Input value={settings[item.k]||item.df} onChange={e=>update(item.k,e.target.value)} className="w-40 text-sm" data-testid={`env-${item.k}`} />
              <p className="text-xs text-muted-foreground flex-1">{item.d}</p>
            </div>
          ))}
          {/* More toggles - second batch */}
          {[
            {k:'user_role_permissions',l:'User Role & Permissions',d:'Activating this switch allows creating new user roles and assigning them permissions based on their role in the User Maintenance > User Role & Permissions section.'},
            {k:'users_approve_own_orders',l:'Users Can Approve their own orders?',d:'Enabling this switch allows approvers to approve their own orders within the system.'},
            {k:'order_on_behalf_of',l:'Order on Behalf of',d:'Enabling this switch allows Buyers and Senior Buyers to place orders on behalf of other Buyers or Senior Buyers within the system.'},
            {k:'order_negative_zero_value',l:'Order Negative And Zero Value',d:'Enabling this switch allows purchase orders to include negative values (indicating credits or returns) or zero values (representing transactions with no cost) within the system.'},
            {k:'show_payment_terms',l:'Show Payment Terms',d:'Enabling this switch will display Supplier Payment Terms on the Purchase Order.'},
            {k:'set_discount_type',l:'Set Discount Type',d:'By activating this switch, requests and purchase orders can include discounts as a value or as a percentage (%). If set to No, the discount will be automatically applied as a percentage.'},
            {k:'account_code_update',l:'Account Code Update',d:'Enabling this switch allows users to update account codes on a Request/Purchase Order that is currently Pending for Approval. (Note: This will not be operational if the RULE Approval is active.)'},
            {k:'activate_group_items',l:'Activate Group Items',d:'The functionality of Group Items resembles that of a Bill of Materials (BOM). It allows you to consolidate multiple items into a single Group, simplifying the ordering process. Group items work only on the request module.'},
            {k:'prefix_po_doc_type',l:'Prefix PO Number with Doc Type Code',d:"When you toggle this option, the 'Document Type' you choose will be added as a prefix to the order number on the Purchase Order report. e.g., 'PO0000123'."},
            {k:'merge_order_line_items',l:'Merge Order Line Items into PO Report',d:'This switch permits the merging of line items with identical Item Code, Description, Due Date, and Supplier Part Number into a single line item in the Purchase Order Report.'},
            {k:'gr_invoice_mandatory',l:'GR Invoice Mandatory',d:'Enabling this switch prevents users from creating an invoice until the goods are received, thereby restricting them from proceeding further.'},
            {k:'supplier_item_price_update',l:'Supplier Item Price Update',d:"This switch will update the supplier's item price with the order item price when an order is issued."},
            {k:'add_punchout_items',l:'Add Punchout Items',d:"This switch will create a catalog item using the first 20 characters of the punchout description when an order is issued. (Note: All punchout items will be added to the category code 'ERA Category'.)"},
            {k:'carbon_emissions',l:'Carbon Emissions',d:'This switch allows users to add or update carbon emissions data for Items, Suppliers, Requests, and Orders.'},
            {k:'bulk_import_items',l:'Bulk Import Items for Requests, Orders, and RFQs',d:"This switch allows users to bulk import item data. The 'Import Items from MS-Excel' option will be displayed in the Line Items > Main Details menu."},
            {k:'accessibility_widget',l:'Accessibility Widget',d:'Enable this setting to provide users with accessibility options such as adjustable text size, high-contrast themes, screen reader support, and other assistive features.'},
            {k:'substitute_item',l:'Substitute Item',d:'This switch allows users to substitute items in an order during receiving (book-in). The substitute item can be entered as a free-text item or added directly to the item database.'},
          ].map(item=>(
            <div key={item.k} className="flex items-start gap-4 py-2 border-b last:border-b-0">
              <Label className="w-56 text-sm pt-1 flex-shrink-0">{item.l}</Label>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Switch checked={settings[item.k]||false} onCheckedChange={v=>update(item.k,v)} data-testid={`env-${item.k}`} />
                <span className={`text-xs font-medium w-6 ${settings[item.k] ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {settings[item.k] ? 'Yes' : 'No'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground flex-1">{item.d}</p>
            </div>
          ))}
          {/* Catalog Item Price - radio */}
          <div className="flex items-start gap-4 py-2 border-t">
            <Label className="w-56 text-sm pt-1 flex-shrink-0">Catalog Item Price</Label>
            <div className="space-y-2">
              {[{v:'main_supplier',l:'Main Supplier Price'},{v:'lowest_supplier',l:'Lowest Supplier Price'},{v:'last_po',l:'Last PO Price'}].map(o=>(
                <label key={o.v} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="catalog_item_price" value={o.v} checked={(settings.catalog_item_price||'main_supplier')===o.v}
                    onChange={e=>update('catalog_item_price',e.target.value)} className="h-4 w-4" data-testid={`env-catalog-price-${o.v}`} />
                  <span className="text-xs">{o.l}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground flex-1">
              Users can choose which supplier price is displayed by default when adding an item from the catalog.
              'Main Supplier Price' shows the price from the Main Supplier. 'Lowest Supplier Price' shows the lowest rate.
              'Last PO Price' shows the price from the most recent purchase order.
            </p>
          </div>
        </div>
      </div>

      {/* Section 14: Savings Configuration */}
      <div className={`border rounded-lg p-6 mb-6 ${isActive('savings-config') ? '' : 'hidden'}`} data-testid="env-savings-config-section">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">14</span>
          Savings Configuration
        </h3>
        <p className="text-xs text-muted-foreground mb-4">Configure how procurement savings are calculated across sourcing events. The baseline method determines the reference price used to measure savings achieved through competitive bidding and negotiation.</p>
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <Label className="w-56 text-sm pt-2 flex-shrink-0">Default Baseline Method</Label>
            <div className="flex-1">
              <select value={settings.savings_config?.default_baseline_method || 'last_purchase_price'}
                onChange={e => update('savings_config', { ...(settings.savings_config || {}), default_baseline_method: e.target.value })}
                className="h-8 w-full max-w-xs rounded-lg border border-input bg-background px-3 text-sm" data-testid="env-savings-baseline-method">
                <option value="last_purchase_price">Last Purchase Price</option>
                <option value="average_price">Average Historical Price</option>
                <option value="budget_price">Budget / Estimated Price</option>
                <option value="first_bid">First Bid Received</option>
                <option value="manual">Manual Entry</option>
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">This method will be pre-selected for all new sourcing events.</p>
            </div>
          </div>
          <div className="flex items-start gap-4 border-t pt-3">
            <Label className="w-56 text-sm pt-1 flex-shrink-0">Allow Event-Level Override</Label>
            <div className="flex items-center gap-2">
              <Switch checked={settings.savings_config?.allow_event_override !== false}
                onCheckedChange={v => update('savings_config', { ...(settings.savings_config || {}), allow_event_override: v })}
                data-testid="env-savings-allow-override" />
              <span className={`text-xs font-medium ${settings.savings_config?.allow_event_override !== false ? 'text-green-600' : 'text-muted-foreground'}`}>
                {settings.savings_config?.allow_event_override !== false ? 'Yes' : 'No'}
              </span>
            </div>
            <p className="text-xs text-muted-foreground flex-1">When enabled, procurement users can select a different baseline method per event. When disabled, all events use the default method above.</p>
          </div>
          <div className="flex items-start gap-4 border-t pt-3">
            <Label className="w-56 text-sm pt-1 flex-shrink-0">Require Override Justification</Label>
            <div className="flex items-center gap-2">
              <Switch checked={settings.savings_config?.require_override_justification || false}
                onCheckedChange={v => update('savings_config', { ...(settings.savings_config || {}), require_override_justification: v })}
                data-testid="env-savings-require-justification" />
              <span className={`text-xs font-medium ${settings.savings_config?.require_override_justification ? 'text-green-600' : 'text-muted-foreground'}`}>
                {settings.savings_config?.require_override_justification ? 'Yes' : 'No'}
              </span>
            </div>
            <p className="text-xs text-muted-foreground flex-1">When enabled, users must provide a written justification when overriding the default baseline method. Required for audit compliance.</p>
          </div>
          <div className="flex items-start gap-4 border-t pt-3">
            <Label className="w-56 text-sm pt-1 flex-shrink-0">Track Cost Avoidance</Label>
            <div className="flex items-center gap-2">
              <Switch checked={settings.savings_config?.track_cost_avoidance !== false}
                onCheckedChange={v => update('savings_config', { ...(settings.savings_config || {}), track_cost_avoidance: v })}
                data-testid="env-savings-track-avoidance" />
              <span className={`text-xs font-medium ${settings.savings_config?.track_cost_avoidance !== false ? 'text-green-600' : 'text-muted-foreground'}`}>
                {settings.savings_config?.track_cost_avoidance !== false ? 'Yes' : 'No'}
              </span>
            </div>
            <p className="text-xs text-muted-foreground flex-1">Track cost avoidance separately from hard savings. Cost avoidance captures prevented cost increases that don't appear as direct savings.</p>
          </div>
        </div>
      </div>

      {/* External Portal URLs */}
      <div className={`border rounded-lg p-5 space-y-4 ${isActive('external-portal-urls') ? '' : 'hidden'}`} data-testid="env-external-portal-urls-section">
        <div className="flex items-center gap-2 mb-1">
          <ExternalLink className="h-5 w-5 text-primary" />
          <div>
            <h3 className="text-base font-semibold">External Portal URLs</h3>
            <p className="text-xs text-muted-foreground">Quick links to public-facing portals. Click to open in a new window.</p>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <div className="flex items-center gap-3">
              <Label className="text-sm w-48 shrink-0">Application URL *</Label>
              <Input value={settings.app_url || ''} placeholder="https://ibuy.cloud"
                onChange={e => update('app_url', e.target.value)}
                className="h-8 text-sm flex-1" data-testid="env-app-url" />
            </div>
            <p className="text-[10px] text-muted-foreground ml-[12.5rem] mt-1">Your customer-facing domain. Used in all outbound emails — supplier invitations, approval notifications, and system alerts. If blank, the system will use the internal hosting URL which may not be correct for your users.</p>
          </div>
          <div className="border-t pt-3"></div>
          <div className="flex items-center gap-3">
            <Label className="text-sm w-48 shrink-0">Supplier Portal</Label>
            <Input value={settings.supplier_portal_url || `${window.location.origin}/supplier-portal`}
              onChange={e => update('supplier_portal_url', e.target.value)}
              className="h-8 text-sm flex-1" />
            <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => window.open(settings.supplier_portal_url || `${window.location.origin}/supplier-portal`, '_blank')} data-testid="open-supplier-portal-btn">
              <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-sm w-48 shrink-0">Public Tenders / Bidding</Label>
            <Input value={settings.public_tenders_url || `${window.location.origin}/public_RFX`}
              onChange={e => update('public_tenders_url', e.target.value)}
              className="h-8 text-sm flex-1" />
            <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => window.open(settings.public_tenders_url || `${window.location.origin}/public_RFX`, '_blank')} data-testid="open-tenders-btn">
              <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open
            </Button>
          </div>
        </div>
      </div>

      {/* Sticky Save Bar — always visible at bottom of viewport */}
      <div className="sticky bottom-0 left-0 right-0 -mx-6 px-6 py-3 mt-6 bg-white/95 backdrop-blur-sm border-t border-zinc-200 shadow-[0_-4px_12px_-4px_rgba(0,0,0,0.08)] flex items-center justify-between z-30">
        <div className="flex items-center gap-2 text-sm">
          {isDirty ? (
            <>
              <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-amber-700 font-medium">Unsaved changes</span>
            </>
          ) : (
            <>
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-zinc-500">All changes saved</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleDiscard} disabled={!isDirty || saving} data-testid="environment-settings-discard-btn">
            <X className="h-4 w-4 mr-1" />Discard
          </Button>
          <Button onClick={handleSave} disabled={saving || !isDirty} data-testid="environment-settings-save-btn" className="min-w-[110px]">
            <Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
